import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Button } from 'primereact/button';
import FormInputText from '../../components/form/FormInputText';
import { OverrideSettingViewModel } from '../../models/waterfall';
import styles from './SimpleExpressionForm.module.css';

interface OverrideSettingFormProps {
  initialValue?: OverrideSettingViewModel;
  onChange?: (setting: OverrideSettingViewModel | null) => void;
  onDelete?: () => void;
  showDelete?: boolean;
}

interface FormValues {
  fieldName: string;
  value: string;
}

const validationSchema = yup.object().shape({
  fieldName: yup.string().trim().required('Property is required'),
  value: yup.string().trim().required('Value is required'),
});

export const OverrideSettingForm: FC<OverrideSettingFormProps> = ({
  initialValue,
  onChange,
  onDelete,
  showDelete = true,
}) => {
  const {
    control,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    defaultValues: {
      fieldName: initialValue?.fieldName || '',
      value: initialValue?.value || '',
    },
  });

  const formValues = watch();

  useEffect(() => {
    if (onChange) {
      if (formValues.fieldName?.trim() && formValues.value?.trim()) {
        onChange({
          fieldName: formValues.fieldName.trim(),
          value: formValues.value.trim(),
        });
      } else {
        onChange(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues.fieldName, formValues.value]);

  return (
    <div className={`p-fluid p-input-filled flex gap-2 text-sm w-full ${styles.form}`}>
      <div className="min-w-42 flex-1">
        <FormInputText
          control={control}
          errors={errors}
          field="fieldName"
          label=""
          placeholder="Field name"
          className="mb-0"
        />
      </div>

      <div className="flex items-center text-gray-400 font-bold" style={{ padding: '0 0.25rem' }}>
        =
      </div>

      <div className="min-w-42 flex-1">
        <FormInputText
          control={control}
          errors={errors}
          field="value"
          label=""
          placeholder="Override value"
          className="mb-0"
        />
      </div>

      {onDelete && showDelete && (
        <Button type="button" icon="pi pi-trash" className="p-button-sm p-button-text p-button-danger" onClick={onDelete} />
      )}
    </div>
  );
};

